import { ensureDocumentFonts } from '../model/fonts';
import { invalidateTextLayouts } from '../model/textLayout';
import { pageOf } from '../model/operations';
import { drawPage } from './renderer';
import { preparePage } from './resources';
import { requestStill } from './video';
import { isActive, sourceTime } from '../model/timeline';

// Export (renderer `studio-art-web-v1`): the saved document painted by the same renderer
// as the stage, at 1× (1080 px wide) or 2×. It refuses to produce a file that would differ
// from the preview: a face that did not load, an image that failed or a tainted canvas
// stop the export with a code the editor explains, and nothing is downloaded.
export const ART_RENDERER_VERSION = 'studio-art-web-v1';
export const EXPORT_FORMATS = { png: { mime: 'image/png', extension: 'png' }, jpeg: { mime: 'image/jpeg', extension: 'jpg' }, mp4: { mime: 'video/mp4', extension: 'mp4' } };
export const EXPORT_SCALES = [1, 2];

export class ArtExportError extends Error {
  constructor(code, details = {}) {
    super(`Art export failed: ${code}`);
    this.code = code;
    this.details = details;
  }
}

// The frame each video of a page shows at `time`, grabbed from its file; with the layers
// whose file could not be read.
async function prepareStills(page, time) {
  const videos = page.layers.filter(layer => layer.type === 'video' && layer.visible && isActive(layer, time));
  const frames = new Map();
  const failed = [];
  await Promise.all(videos.map(async layer => {
    const canvas = await requestStill(layer.source?.url, sourceTime(layer, time)).promise;
    if (canvas) frames.set(layer.id, canvas); else failed.push(layer.id);
  }));
  return { frames, failed };
}

export async function renderArtBlob(doc, { pageId = null, format = 'png', scale = 1, quality = 0.92 } = {}) {
  const page = pageOf(doc, pageId);
  const output = EXPORT_FORMATS[format] || EXPORT_FORMATS.png;
  const fonts = await ensureDocumentFonts(doc, page.id);
  if (!fonts.ok) throw new ArtExportError('fonts', { families: fonts.missing });
  // Lines measured before a face arrived would break differently: measure again.
  invalidateTextLayouts();
  const { failed } = await preparePage(page, scale);
  if (failed.length) throw new ArtExportError('images', { layerIds: failed });
  // 2.13.0: a page with videos shows them as at its start.
  const stills = await prepareStills(page, 0);
  if (stills.failed.length) throw new ArtExportError('videos', { layerIds: stills.failed });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(doc.width * scale);
  canvas.height = Math.round(doc.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ArtExportError('render');
  drawPage(ctx, doc, page, { scale, placeholders: false, frames: layer => stills.frames.get(layer.id) || null });
  const blob = await new Promise((resolve, reject) => {
    try { canvas.toBlob(result => (result ? resolve(result) : reject(new ArtExportError('render'))), output.mime, quality); }
    catch (failure) { reject(new ArtExportError(failure?.name === 'SecurityError' ? 'tainted' : 'render')); }
  });
  return { blob, width: canvas.width, height: canvas.height, mime: output.mime, extension: output.extension, pageId: page.id };
}

export const artFileName = (title, extension, suffix = '') => {
  const base = (title || 'arte').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'arte';
  return `${base}${suffix}.${extension}`;
};

export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
